import React, { useMemo, useState } from 'react';
import { Search, Clock, ExternalLink, Play, X, ChevronDown, Youtube } from 'lucide-react';
import { recipes, categoryMeta, RecipeCategory } from '../data/recipes';
import { motion, AnimatePresence } from 'framer-motion';
const CUISINES = ['All', 'Indian', 'Fusion', 'Mexican', 'Asian', 'Italian', 'American'] as const;
// Fallback YouTube search links when no channel recipes match
const CUISINE_FALLBACK: Record<string, {
  title: string;
  searchUrl: string;
  description: string;
}[]> = {
  Italian: [{
    title: 'Vegetarian Pasta Primavera',
    searchUrl: 'https://www.youtube.com/results?search_query=vegetarian+pasta+primavera',
    description: 'Classic Italian pasta with seasonal vegetables'
  }, {
    title: 'Margherita Pizza from Scratch',
    searchUrl: 'https://www.youtube.com/results?search_query=vegetarian+margherita+pizza+recipe',
    description: 'Authentic Neapolitan-style pizza'
  }, {
    title: 'Creamy Mushroom Risotto',
    searchUrl: 'https://www.youtube.com/results?search_query=vegetarian+mushroom+risotto',
    description: 'Rich, creamy arborio rice with mushrooms'
  }],
  American: [{
    title: 'Veggie Burger',
    searchUrl: 'https://www.youtube.com/results?search_query=homemade+vegetarian+veggie+burger',
    description: 'Hearty plant-based burger patty'
  }, {
    title: 'Mac and Cheese',
    searchUrl: 'https://www.youtube.com/results?search_query=vegetarian+mac+and+cheese',
    description: 'Creamy homemade macaroni and cheese'
  }, {
    title: 'Vegetarian Chili',
    searchUrl: 'https://www.youtube.com/results?search_query=vegetarian+chili+recipe',
    description: 'Hearty bean and vegetable chili'
  }],
  Mediterranean: [{
    title: 'Classic Hummus',
    searchUrl: 'https://www.youtube.com/results?search_query=homemade+hummus+recipe',
    description: 'Creamy chickpea dip with tahini'
  }, {
    title: 'Falafel from Scratch',
    searchUrl: 'https://www.youtube.com/results?search_query=vegetarian+falafel+recipe',
    description: 'Crispy chickpea falafel balls'
  }, {
    title: 'Greek Salad',
    searchUrl: 'https://www.youtube.com/results?search_query=authentic+greek+salad+recipe',
    description: 'Fresh cucumber, tomato and feta salad'
  }],
  All: [{
    title: 'Search BeVegToday on YouTube',
    searchUrl: 'https://www.youtube.com/c/BeVegToday',
    description: 'Browse all recipes on the BeVegToday channel'
  }]
};
export function RecipeSuggester() {
  const [ingredientInput, setIngredientInput] = useState('');
  const [ingredientTags, setIngredientTags] = useState<string[]>([]);
  const [cuisine, setCuisine] = useState<string>('All');
  const [category, setCategory] = useState<RecipeCategory | 'All'>('All');
  const [maxTime, setMaxTime] = useState(60);
  const [results, setResults] = useState(recipes);
  const [hasSearched, setHasSearched] = useState(false);
  // Dynamically compute available categories based on selected cuisine
  const availableCategories = useMemo(() => {
    const cuisineFiltered = cuisine === 'All' ? recipes : recipes.filter(r => r.cuisine === cuisine);
    const catSet = new Set<RecipeCategory>();
    cuisineFiltered.forEach(r => r.categories.forEach(c => catSet.add(c)));
    return Array.from(catSet).sort();
  }, [cuisine]);
  const handleCuisineChange = (newCuisine: string) => {
    setCuisine(newCuisine);
    if (category !== 'All') {
      const stillValid = newCuisine === 'All' ? true : recipes.some(r => r.cuisine === newCuisine && r.categories.includes(category as RecipeCategory));
      if (!stillValid) setCategory('All');
    }
  };
  const addTag = (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (trimmed && !ingredientTags.includes(trimmed)) setIngredientTags(prev => [...prev, trimmed]);
    setIngredientInput('');
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(ingredientInput);
    }
  };
  const removeTag = (tag: string) => setIngredientTags(prev => prev.filter(t => t !== tag));
  const handleSearch = () => {
    setHasSearched(true);
    const filtered = recipes.filter(recipe => {
      const matchesIngredients = ingredientTags.length === 0 || ingredientTags.some(tag => recipe.ingredients.some(ing => ing.toLowerCase().includes(tag)) || recipe.name.toLowerCase().includes(tag));
      const matchesCuisine = cuisine === 'All' || recipe.cuisine === cuisine;
      const matchesCategory = category === 'All' || recipe.categories.includes(category as RecipeCategory);
      const matchesTime = recipe.cookTime <= maxTime;
      return matchesIngredients && matchesCuisine && matchesCategory && matchesTime;
    });
    setResults(filtered);
  };
  const handleReset = () => {
    setIngredientTags([]);
    setIngredientInput('');
    setCuisine('All');
    setCategory('All');
    setMaxTime(60);
    setResults(recipes);
    setHasSearched(false);
  };
  // Get fallback suggestions when no channel results
  const fallbackSuggestions = useMemo(() => {
    if (!hasSearched || results.length > 0) return [];
    const key = cuisine in CUISINE_FALLBACK ? cuisine : 'All';
    return CUISINE_FALLBACK[key] || CUISINE_FALLBACK['All'];
  }, [hasSearched, results.length, cuisine]);
  return <section id="finder" className="py-20 px-4 bg-gradient-to-br from-green-50 to-orange-50" data-id="element-262">
      <div className="max-w-5xl mx-auto" data-id="element-263">
        <div className="text-center mb-12" data-id="element-264">
          <h2 className="text-3xl md:text-5xl font-bold text-stone-900 mb-4" style={{
          fontFamily: 'Poppins, sans-serif'
        }} data-id="element-265">
            🔍 Find Your Recipe
          </h2>
          <p className="text-stone-500 text-lg max-w-xl mx-auto" data-id="element-266">
            Search from BeVegToday's recipe collection by ingredients, cuisine,
            or category.
          </p>
        </div>

        {/* Search Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-6 md:p-8 mb-10" data-id="element-267">
          <div className="space-y-6" data-id="element-268">
            {/* Ingredient Tags Input */}
            <div data-id="element-269">
              <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-270">
                🥬 What's in your kitchen?
                <span className="font-normal text-stone-400 ml-2" data-id="element-271">
                  Press Enter or comma to add
                </span>
              </label>
              <div className="flex flex-wrap gap-2 p-3 border border-stone-200 rounded-xl focus-within:border-green-400 focus-within:ring-2 focus-within:ring-green-100 min-h-[52px] bg-stone-50" data-id="element-272">
                {ingredientTags.map(tag => <span key={tag} className="flex items-center gap-1 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full" data-id="element-273">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-red-500 transition-colors" data-id="element-274">
                      <X size={14} data-id="element-275" />
                    </button>
                  </span>)}
                <input type="text" value={ingredientInput} onChange={e => setIngredientInput(e.target.value)} onKeyDown={handleKeyDown} onBlur={() => ingredientInput.trim() && addTag(ingredientInput)} placeholder={ingredientTags.length === 0 ? 'e.g. paneer, tofu, spinach...' : 'Add more...'} className="flex-1 min-w-[120px] outline-none bg-transparent text-stone-700 placeholder-stone-400 text-sm" data-id="element-276" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4" data-id="element-277">
              {/* Cuisine */}
              <div data-id="element-278">
                <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-279">
                  🌍 Cuisine
                </label>
                <div className="relative" data-id="element-280">
                  <select value={cuisine} onChange={e => handleCuisineChange(e.target.value)} className="w-full appearance-none border border-stone-200 rounded-xl px-4 py-3 text-stone-700 bg-stone-50 focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none font-medium" data-id="element-281">
                    {CUISINES.map(c => <option key={c} value={c} data-id="element-282">
                        {c}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={18} data-id="element-283" />
                </div>
              </div>

              {/* Category — filtered by cuisine */}
              <div data-id="element-284">
                <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-285">
                  📂 Category
                  {cuisine !== 'All' && <span className="ml-2 text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-id="element-286">
                      {availableCategories.length} for {cuisine}
                    </span>}
                </label>
                <div className="relative" data-id="element-287">
                  <select value={category} onChange={e => setCategory(e.target.value as RecipeCategory | 'All')} className="w-full appearance-none border border-stone-200 rounded-xl px-4 py-3 text-stone-700 bg-stone-50 focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none font-medium" data-id="element-288">
                    <option value="All" data-id="element-289">All Categories</option>
                    {availableCategories.map(c => <option key={c} value={c} data-id="element-290">
                        {categoryMeta[c].emoji} {categoryMeta[c].label}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={18} data-id="element-291" />
                </div>
              </div>

              {/* Time */}
              <div data-id="element-292">
                <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-293">
                  ⏱️ Max Cook Time:{' '}
                  <span className="text-green-600" data-id="element-294">{maxTime} min</span>
                </label>
                <input type="range" min={5} max={60} step={5} value={maxTime} onChange={e => setMaxTime(Number(e.target.value))} className="w-full h-2 bg-stone-200 rounded-full appearance-none cursor-pointer accent-green-600 mt-3" data-id="element-295" />
                <div className="flex justify-between text-xs text-stone-400 mt-1" data-id="element-296">
                  <span data-id="element-297">5 min</span>
                  <span data-id="element-298">60 min</span>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3" data-id="element-299">
              <button onClick={handleSearch} className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl transition-colors shadow-lg shadow-green-600/20 text-lg" data-id="element-300">
                <Search size={20} data-id="element-301" /> Find Recipes
              </button>
              {hasSearched && <button onClick={handleReset} className="px-6 border-2 border-stone-200 hover:border-stone-300 text-stone-600 font-bold rounded-xl transition-colors" data-id="element-302">
                  Reset
                </button>}
            </div>
          </div>
        </div>

        {/* Results */}
        <AnimatePresence mode="wait" data-id="element-303">
          {hasSearched && <motion.div key="results" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} data-id="element-304">
              <div className="flex items-center justify-between mb-6" data-id="element-305">
                <h3 className="text-xl font-bold text-stone-800" style={{
              fontFamily: 'Poppins, sans-serif'
            }} data-id="element-306">
                  {results.length > 0 ? `${results.length} BeVegToday recipes found` : 'No BeVegToday recipes found'}
                </h3>
              </div>

              {results.length === 0 ? <div data-id="element-307">
                  {/* No results message */}
                  <div className="text-center py-10 bg-white rounded-2xl border border-stone-100 mb-6" data-id="element-308">
                    <p className="text-4xl mb-3" data-id="element-309">🥺</p>
                    <p className="text-xl font-bold text-stone-700 mb-2" data-id="element-310">
                      No BeVegToday recipes match
                    </p>
                    <p className="text-stone-400 mb-4" data-id="element-311">
                      We don't have {cuisine !== 'All' ? cuisine : ''} recipes
                      on the channel yet.
                    </p>
                    <p className="text-stone-500 text-sm" data-id="element-312">
                      Here are some suggestions from other YouTube channels:
                    </p>
                  </div>

                  {/* Fallback YouTube suggestions */}
                  <div className="grid md:grid-cols-3 gap-4" data-id="element-313">
                    {fallbackSuggestions.map((suggestion, i) => <motion.a key={i} href={suggestion.searchUrl} target="_blank" rel="noopener noreferrer" initial={{
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: i * 0.1
              }} className="group bg-white border-2 border-stone-100 hover:border-red-300 rounded-2xl p-5 transition-all hover:shadow-lg" data-id="element-314">
                        <div className="flex items-start gap-3 mb-3" data-id="element-315">
                          <div className="bg-red-100 p-2 rounded-lg flex-shrink-0" data-id="element-316">
                            <Youtube size={20} className="text-red-600" data-id="element-317" />
                          </div>
                          <div data-id="element-318">
                            <h4 className="font-bold text-stone-900 group-hover:text-red-600 transition-colors leading-snug" style={{
                      fontFamily: 'Poppins, sans-serif'
                    }} data-id="element-319">
                              {suggestion.title}
                            </h4>
                            <p className="text-stone-500 text-xs mt-1" data-id="element-320">
                              {suggestion.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-red-500 text-xs font-bold" data-id="element-321">
                          <ExternalLink size={12} data-id="element-322" /> Search on YouTube
                        </div>
                      </motion.a>)}
                  </div>
                </div> : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-323">
                  {results.map((recipe, index) => <motion.a key={recipe.id} href={recipe.videoUrl} target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.06
            }} className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow border border-stone-100 flex flex-col" data-id="element-324">
                      <div className="relative aspect-video overflow-hidden bg-stone-100" data-id="element-325">
                        <img src={`https://img.youtube.com/vi/${recipe.videoId}/maxresdefault.jpg`} alt={recipe.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => {
                  ;
                  (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${recipe.videoId}/hqdefault.jpg`;
                }} data-id="element-326" />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-327">
                          <div className="bg-red-600 text-white p-3 rounded-full shadow-lg" data-id="element-328">
                            <Play size={18} fill="white" data-id="element-329" />
                          </div>
                        </div>
                        {recipe.isNoOnionGarlic && <span className="absolute top-2 left-2 bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-330">
                            Jain ✓
                          </span>}
                      </div>
                      <div className="p-4 flex-1 flex flex-col" data-id="element-331">
                        <h3 className="font-bold text-stone-900 mb-1 line-clamp-2 leading-snug" style={{
                  fontFamily: 'Poppins, sans-serif'
                }} data-id="element-332">
                          {recipe.name}
                        </h3>
                        <p className="text-stone-500 text-sm line-clamp-2 mb-3 flex-1" data-id="element-333">
                          {recipe.description}
                        </p>
                        <div className="flex items-center justify-between pt-3 border-t border-stone-100" data-id="element-334">
                          <span className="flex items-center gap-1 text-stone-500 text-sm" data-id="element-335">
                            <Clock size={14} data-id="element-336" /> {recipe.cookTime} min
                          </span>
                          <span className="text-xs font-semibold text-stone-400" data-id="element-337">
                            {recipe.cuisine}
                          </span>
                          <span className="flex items-center gap-1 text-red-500 text-xs font-bold" data-id="element-338">
                            <ExternalLink size={12} data-id="element-339" /> Watch
                          </span>
                        </div>
                      </div>
                    </motion.a>)}
                </div>}
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}